<p-toast></p-toast>
<p-confirmDialog></p-confirmDialog>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell>
    <app-page-hero
        backLink="/company/users-and-roles"
        eyebrow="Company Administration"
        [title]="isEditMode ? 'Edit Custom Role: ' + (roleName || 'Role #' + roleId) : 'Create Custom Role'"
        [subtitle]="isEditMode ? 'Modify identity, baseline stitched roles, and permission fine-tuning.' : 'Define a new custom role by stitching standard baseline templates and handpicking granular permissions.'">
        
        <div nav class="flex gap-2 align-items-center">
            <a pButton routerLink="/company/users-and-roles" [queryParams]="{ tab: 'roles' }" label="Users & Roles" icon="pi pi-arrow-left" [outlined]="true" severity="secondary"></a>
            <p-tag value="Custom Role" severity="success" icon="pi pi-check"></p-tag>
            <p-tag *ngIf="roleId" [value]="'ID ' + roleId" severity="info"></p-tag>
        </div>

        <div actions class="flex gap-2">
            <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="onCancel()"></p-button>
            <p-button label="Save Custom Role" icon="pi pi-check" severity="success" (onClick)="onSave()" [loading]="isSaving" [disabled]="!roleName?.trim() || selectedPermissionIds.size === 0 || isSaving"></p-button>
        </div>
    </app-page-hero>

    <app-page-metric-strip [metrics]="builderMetrics"></app-page-metric-strip>

    <!-- 1. Role Details Form Section -->
    <app-page-form-section
        icon="pi pi-id-card"
        title="1. Role Details"
        subtitle="Define the basic identity, title, and operational description for this custom role.">
        
        <div class="form-grid">
            <div class="form-field field-grow">
                <label for="roleName">Role Name <span class="required text-danger">*</span></label>
                <input id="roleName" pInputText [(ngModel)]="roleName" placeholder="e.g. Customs & Invoicing Specialist" />
            </div>
            <div class="form-field field-grow">
                <label for="description">Description</label>
                <input id="description" pInputText [(ngModel)]="description" placeholder="Briefly describe what this role allows..." />
            </div>
            <div class="form-field">
                <label for="scope">Role Scope</label>
                <input id="scope" pInputText [(ngModel)]="roleScope" disabled />
            </div>
        </div>
    </app-page-form-section>

    <!-- 2. Multi-Role Stitching Section -->
    <app-page-form-section
        icon="pi pi-link"
        title="2. Stitch Standard Roles (Baseline Templates)"
        subtitle="Pick one or multiple standard roles as building blocks. Their permissions are automatically combined into your baseline.">
        
        <div actions class="flex gap-2 align-items-center">
            <p-tag [value]="selectedStandardRoleIds.size + ' standard roles selected (' + countFromStitchedRoles + ' permissions inherited)'" severity="info"></p-tag>
        </div>

        <div class="standard-roles-grid">
            <div *ngFor="let standard of standardRoles"
                 class="role-card"
                 [class.selected]="isStandardRoleSelected(standard.roleId)"
                 (click)="toggleStandardRole(standard)">
                <div class="role-card-header">
                    <div class="role-card-title">
                        <i class="pi" [ngClass]="isStandardRoleSelected(standard.roleId) ? 'pi-check-circle text-primary' : 'pi-circle'"></i>
                        <strong>{{ standard.roleName }}</strong>
                    </div>
                    <p-tag [value]="standard.permissionCount + ' perms'" severity="info"></p-tag>
                </div>
                <p class="role-card-desc">{{ standard.description || 'Predefined system role template.' }}</p>
            </div>
        </div>
    </app-page-form-section>

    <!-- 3. Handpick Permissions Section -->
    <app-page-form-section
        icon="pi pi-sliders-h"
        title="3. Handpick Permissions"
        subtitle="Fine-tune individual permissions. Add extra capabilities beyond your stitched roles or inspect granted access.">
        
        <div actions class="flex gap-2">
            <p-button label="Select All in Category" icon="pi pi-check" size="small" [outlined]="true" (onClick)="selectAllInActiveModule()"></p-button>
            <p-button label="Clear Category" icon="pi pi-times" size="small" [outlined]="true" severity="secondary" (onClick)="clearAllInActiveModule()"></p-button>
        </div>

        <!-- Toolbar & Filter Pills -->
        <div class="permission-toolbar">
            <div class="search-box">
                <i class="pi pi-search"></i>
                <input pInputText [(ngModel)]="searchTerm" placeholder="Search by permission code, name, or action (e.g. petty-cash, release)..." />
            </div>
            <div class="view-filter-buttons">
                <button pButton type="button" label="All ({{ allPermissions.length }})"
                        [outlined]="permissionFilter !== 'ALL'" size="small" (click)="permissionFilter = 'ALL'"></button>
                <button pButton type="button" label="Selected ({{ selectedPermissionIds.size }})"
                        [outlined]="permissionFilter !== 'SELECTED'" size="small" (click)="permissionFilter = 'SELECTED'"></button>
                <button pButton type="button" label="Stitched ({{ countFromStitchedRoles }})"
                        [outlined]="permissionFilter !== 'STITCHED'" size="small" severity="info" (click)="permissionFilter = 'STITCHED'"></button>
                <button pButton type="button" label="Handpicked ({{ countHandpicked }})"
                        [outlined]="permissionFilter !== 'HANDPICKED'" size="small" severity="warn" (click)="permissionFilter = 'HANDPICKED'"></button>
            </div>
        </div>

        <!-- Module Navigation Tabs -->
        <div class="module-chips-scroll">
            <span *ngFor="let mod of modules"
                  class="module-chip"
                  [class.active]="activeModule === mod"
                  (click)="activeModule = mod">
                {{ mod }}
            </span>
        </div>

        <!-- Permissions Checklist Grid -->
        <div class="permissions-list-scroll">
            <div *ngIf="filteredPermissions.length === 0" class="empty-permissions">
                <i class="pi pi-info-circle text-4xl mb-2 text-secondary"></i>
                <p>No permissions match your search or filter.</p>
            </div>

            <div *ngFor="let perm of filteredPermissions"
                 class="permission-row"
                 [class.row-selected]="isPermissionSelected(perm.permissionId)"
                 (click)="togglePermission(perm)">
                <div class="perm-check">
                    <p-checkbox
                        [binary]="true"
                        [ngModel]="isPermissionSelected(perm.permissionId)"
                        (click)="$event.stopPropagation()"
                        (onChange)="togglePermission(perm)">
                    </p-checkbox>
                </div>
                <div class="perm-info">
                    <div class="perm-title-row">
                        <span class="perm-name">{{ perm.permissionName }}</span>
                        <span class="perm-code text-secondary"><code>{{ perm.permissionCode }}</code></span>
                    </div>
                    <div class="perm-desc text-secondary" *ngIf="perm.description">{{ perm.description }}</div>
                </div>
                <div class="perm-badge">
                    <p-tag *ngIf="isPermissionSelected(perm.permissionId) && getStitchedSourceRoleName(perm.permissionId)"
                           icon="pi pi-link"
                           [value]="'Stitched: ' + getStitchedSourceRoleName(perm.permissionId)"
                           severity="info">
                    </p-tag>
                    <p-tag *ngIf="isPermissionSelected(perm.permissionId) && !getStitchedSourceRoleName(perm.permissionId)"
                           icon="pi pi-plus"
                           value="Handpicked"
                           severity="warn">
                    </p-tag>
                </div>
            </div>
        </div>
    </app-page-form-section>

    <!-- Sticky Footer Summary & Actions -->
    <div class="sticky-footer-bar">
        <div class="summary-pills">
            <span class="summary-item">
                <strong class="text-primary">{{ selectedPermissionIds.size }}</strong> Total Effective Permissions
            </span>
            <span class="summary-sep">|</span>
            <span class="summary-sub">
                <i class="pi pi-link text-info mr-1"></i> {{ countFromStitchedRoles }} from stitched roles
            </span>
            <span class="summary-sep">|</span>
            <span class="summary-sub">
                <i class="pi pi-plus text-warn mr-1"></i> {{ countHandpicked }} handpicked additions
            </span>
        </div>
        <div class="footer-buttons flex gap-2">
            <p-button label="Cancel" severity="secondary" [outlined]="true" (onClick)="onCancel()" [disabled]="isSaving"></p-button>
            <p-button label="Save Custom Role" icon="pi pi-check" severity="success" (onClick)="onSave()" [loading]="isSaving" [disabled]="!roleName?.trim() || selectedPermissionIds.size === 0 || isSaving"></p-button>
        </div>
    </div>
</app-page-shell>
